import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router";
import { usePortraitsTranslator } from "../i18n/translator";
import { Filter, FILTERS } from "./galleryFilters";
import { GalleryTile } from "./GalleryTile";
import { useListPortraits } from "./useAdminPortraits";

const PAGE_SIZE = 48;

export const AdminGallery = ({
  filter,
  onFilterChange,
  selectedUidNumber,
}: {
  filter: Filter;
  onFilterChange: (filter: Filter) => void;
  selectedUidNumber: number | undefined;
}) => {
  const translator = usePortraitsTranslator();
  const navigate = useNavigate();
  const { data, isError } = useListPortraits();
  const [shown, setShown] = useState(PAGE_SIZE);

  if (isError) {
    return (
      <Alert severity="error">{translator("unknownErrorTryAgainLater")}</Alert>
    );
  }
  if (!data) {
    return <CircularProgress aria-busy />;
  }

  const matching = data.employees.filter(FILTERS[filter]);
  // Previous and next may step beyond the tiles shown so far
  const shownWithSelected = Math.max(
    shown,
    matching.findIndex(({ uidNumber }) => uidNumber === selectedUidNumber) + 1,
  );
  const filterLabels: Record<Filter, string> = {
    chosen: translator("filterChosen"),
    flagged: translator("filterFlagged"),
    notChosenYet: translator("notChosenYet"),
  };

  return (
    <Stack spacing={3}>
      <Stack spacing={1}>
        <Typography variant="h5" component="h1">
          {translator("galleryTitle")}
        </Typography>
        <Typography>{translator("galleryHint")}</Typography>
      </Stack>
      <Autocomplete
        options={data.employees}
        getOptionLabel={({ name, ldapName }) =>
          ldapName ? `${name} (${ldapName})` : name
        }
        getOptionKey={({ uidNumber }) => uidNumber}
        onChange={(_, employee) => {
          if (employee) {
            void navigate(`/admin/${employee.uidNumber}`);
          }
        }}
        renderInput={(params) => (
          <TextField {...params} label={translator("findColleague")} />
        )}
        sx={{ maxWidth: 480 }}
      />
      <ToggleButtonGroup
        exclusive
        value={filter}
        aria-label={translator("filterLabel")}
        onChange={(_, value: Filter | null) => {
          if (value) {
            onFilterChange(value);
            setShown(PAGE_SIZE);
          }
        }}
      >
        {(Object.keys(FILTERS) as Filter[]).map((key) => (
          <ToggleButton key={key} value={key}>
            {filterLabels[key]} ({data.employees.filter(FILTERS[key]).length})
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      {matching.length === 0 ? (
        <Alert severity="info">{translator("nothingToShow")}</Alert>
      ) : (
        <Box
          display="grid"
          gridTemplateColumns="repeat(auto-fill, minmax(min(100%, 280px), 280px))"
          gap={2}
        >
          {matching.slice(0, shownWithSelected).map((employee) => (
            <GalleryTile
              key={employee.uidNumber}
              employee={employee}
              selected={employee.uidNumber === selectedUidNumber}
            />
          ))}
        </Box>
      )}
      {matching.length > shownWithSelected && (
        <Button
          sx={{ alignSelf: "start" }}
          onClick={() => setShown(shownWithSelected + PAGE_SIZE)}
        >
          {translator("showMore")}
        </Button>
      )}
    </Stack>
  );
};
